import { useState } from 'react';
import { Send } from 'lucide-react';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { EmptyState } from '@/shared/components/ui/EmptyState';
import { timeAgo } from '@/shared/utils/utils';
import type { DemoComment } from '../types';

interface CommentThreadProps {
  comments: DemoComment[];
  posting?: boolean;
  onAdd: (text: string) => void;
}

/** Reusable comment thread (list + composer). */
export function CommentThread({ comments, posting, onAdd }: CommentThreadProps) {
  const [text, setText] = useState('');

  const submit = () => {
    const t = text.trim();
    if (!t || posting) return;
    onAdd(t);
    setText('');
  };

  return (
    <div className="flex flex-col gap-3">
      {comments.length === 0 ? (
        <EmptyState title="No comments yet" description="Start the conversation below." />
      ) : (
        <ul className="flex flex-col gap-2">
          {comments.map((c) => (
            <li key={c.id} className="rounded-card border border-outline bg-surface px-3 py-2">
              <div className="flex items-center justify-between">
                <span className="text-data-md font-medium text-on-surface">{c.author_name || 'User'}</span>
                <span className="text-label-caps uppercase text-on-surface-variant/70">{timeAgo(c.created_at)}</span>
              </div>
              <p className="mt-0.5 whitespace-pre-wrap text-body-sm text-on-surface-variant">{c.comment}</p>
            </li>
          ))}
        </ul>
      )}
      <div className="flex items-center gap-2">
        <Input
          name="demo-comment"
          placeholder="Write a comment…"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && submit()}
        />
        <Button size="icon" aria-label="Send comment" loading={posting} onClick={submit}>
          <Send className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
